import React, { useState, useEffect } from "react";
import API from "../../../Utils/api";
import UploadImg from "../../../Utils/UploadImg";
import toast from "react-hot-toast";
const EditCategoryModal = ({ category, onClose, onSuccess }) => {
  const [form, setForm] = useState({
    category: "",
    description: "",
    keyPoints: [""],
  });
  const [imageFile, setImageFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (category) {
      setForm({
        category: category.category || "",
        description: category.description || "",
        keyPoints: category.keyPoints?.length > 0 ? category.keyPoints : [""],
      });
      setPreview(category.image || null);
    }
  }, [category]);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleKeyPointChange = (index, value) => {
    const updated = [...form.keyPoints];
    updated[index] = value;
    setForm((prev) => ({ ...prev, keyPoints: updated }));
  };

  const addKeyPoint = () => {
    setForm((prev) => ({ ...prev, keyPoints: [...prev.keyPoints, ""] }));
  };

  const removeKeyPoint = (index) => {
    const updated = form.keyPoints.filter((_, i) => i !== index);
    setForm((prev) => ({ ...prev, keyPoints: updated }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      let imageUrl = category.image;

      // Upload new image only if changed
      if (imageFile) {
        imageUrl = await UploadImg(imageFile);
      }

      const filteredKeyPoints = form.keyPoints.filter((k) => k.trim() !== "");

      await API.put(`/category/update/${category._id}`, {
        category: form.category,
        image: imageUrl,
        description: form.description,
        keyPoints: filteredKeyPoints,
      });

      onSuccess();
      toast.success("Your Category has been Updated successfully! 🎉");
    } catch (err) {
      setError(err.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 bg-green-50 border-b border-gray-100">
          <h2 className="text-xl font-bold text-gray-800">Edit Category</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-500 transition"
          >
            ✕
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category Image
            </label>
            <div
              onClick={() => document.getElementById("edit-cat-image").click()}
              className="border-2 border-dashed border-gray-300 hover:border-green-400 rounded-xl h-40 flex items-center justify-center cursor-pointer transition overflow-hidden"
            >
              {preview ? (
                <img
                  src={preview}
                  alt="preview"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="text-center text-gray-400">
                  <p className="text-3xl">🖼️</p>
                  <p className="text-sm mt-1">Click to change image</p>
                </div>
              )}
            </div>
            <input
              id="edit-cat-image"
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="hidden"
            />
          </div>

          {/* Category Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="category"
              value={form.category}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 resize-none"
            />
          </div>

          {/* Key Points */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Key Points
            </label>
            <div className="space-y-2">
              {form.keyPoints.map((point, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={point}
                    onChange={(e) =>
                      handleKeyPointChange(index, e.target.value)
                    }
                    placeholder={`Key point ${index + 1}`}
                    className="flex-1 border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
                  />
                  {form.keyPoints.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeKeyPoint(index)}
                      className="w-9 h-9 flex items-center justify-center rounded-lg bg-red-50 hover:bg-red-100 text-red-500 transition"
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addKeyPoint}
              className="mt-2 text-sm text-green-600 hover:text-green-700 font-medium"
            >
              + Add Key Point
            </button>
          </div>

          {/* Submit */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-2.5 rounded-lg bg-green-600 hover:bg-green-700 text-white font-medium transition disabled:opacity-60"
            >
              {loading ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditCategoryModal;
